<script setup lang="ts">
import { computed, ref, toValue } from 'vue'
import { useI18n } from 'vue-i18n'
import type { DeliveryOrder } from '../../../../../Types/deliveryOrder'
import { DeliveryOrderStatus } from '../../../../../Constants/DeliveryOrderStatus'
import useApi from '../../../Composables/useApi'
import FormCreator from '../../UI/Form/FormCreator.vue'
import ContractDeliveryOrderSignature from './ContractDeliveryOrderSignature.vue'
import ButtonAll from '../../UI/Button/ButtonAll.vue'
import BlockContent from '../../UI/Content/BlockContent.vue'
import FieldCreator from '../../UI/Form/FieldCreator.vue'
import type { DocumentSign } from '../../../../../Types/document'

type FormCreatorType = InstanceType<typeof FormCreator>
type FormDef = {
  operationalAt?: Object
}

const { d, n, t } = useI18n()
const { deleteJSON, patchJSON } = useApi()

const props = defineProps<{
  delivery: DeliveryOrder
  contractNumber: string
  contractId: number
  uuid: string | null | undefined
}>()

const emit = defineEmits<{
  update: [deliveryOrder: DeliveryOrder]
  reload: []
}>()

const formRef = ref<FormCreatorType | null>(null)
const formDef = ref<FormDef | null>(null)
const fieldLoaded = ref(false)
const sending = ref(false)

const deliveryNumber = computed(() => props.contractNumber + '-' + toValue(props.delivery).id)
const hasEditableField = computed(() => !props.delivery.deliveredAt || formDef.value?.operationalAt)
const documentSign = computed<DocumentSign | null>(() => props.delivery.documentAssoc?.document?.documentSign ?? null)

function setFormDef(value: object) {
  formDef.value = value
}

function setFieldLoaded(value: boolean) {
  fieldLoaded.value = value
}

function setSending(value: boolean) {
  sending.value = value
}

async function updateDeliveryOrder() {
  if (formRef.value === null) {
    return
  }

  const formDatas = formRef.value.datas as { contractId?: number }
  formDatas.contractId = props.contractId
  await formRef.value.methods('patch')
}

async function cancelDelivery() {
  if (documentSign.value === null) {
    return
  }

  await patchJSON(`deliveryorder/${toValue(props.delivery).id}/cancel`, {}, {})
  location.reload()
}

async function deleteDelivery() {
  try {
    await deleteJSON(`deliveryorder/${toValue(props.delivery).id}`, { contractId: props.contractId }, {})
    location.reload()
  } catch (error) {
    alert(error)
  }
}
</script>

<template>
  <div>
    <FormCreator
      v-if="delivery.equipments.length != 0"
      :id="delivery.id"
      ref="formRef"
      endpoint="deliveryorder"
      method="patch"
      :uuid="uuid"
      :params="{ deliveryOrderId: delivery.id }"
      class-to-add="d-none"
      @form-build="setFieldLoaded"
      @def-build="setFormDef"
      @is-sending="setSending"
      @form-return="emit('update', $event)"
    />

    <BlockContent>
      <div class="d-flex justify-content-between align-items-center">
        <h4>{{ t('delivery.title', { deliveryNumber }) }}</h4>
        <div>
          <button
            v-if="delivery.documentAssoc === null"
            type="button"
            class="btn btn-info"
            e2e-button="deliveryReload"
            @click="emit('reload')"
          >
            {{ t('delivery.modifyView') }}
          </button>
          <button
            v-if="delivery.status !== DeliveryOrderStatus.SIGNED"
            type="button"
            class="btn btn-danger ml-2 pl-3"
            e2e-button="deliveryDelete"
            @click="deleteDelivery"
          >
            {{ t('delivery.delete') }}
          </button>
          <button
            v-if="delivery.documentAssoc !== null || delivery.status === DeliveryOrderStatus.SIGNED"
            type="button"
            class="btn btn-warning pl-3 ml-2"
            e2e-button="deliveryCancel"
            @click="cancelDelivery"
          >
            {{ delivery.leaseback ? t('delivery.leasebackCancel') : t('delivery.cancel') }}
          </button>
        </div>
      </div>
      <div v-if="delivery.equipments.length != 0" style="overflow-x: auto">
        <table class="table table-striped fs-16">
          <thead>
            <tr>
              <th class="col-md-2">{{ t('equipment.fields.supplier.title') }}</th>
              <th>{{ t('equipment.fields.brand.label') }}</th>
              <th>{{ t('equipment.fields.model.label') }}</th>
              <th>{{ t('equipment.fields.serialNumber.label') }}</th>
              <th class="text-right">{{ t('equipment.fields.unitAmount.label') }}</th>
              <th class="text-right">{{ t('equipment.fields.quantity.label') }}</th>
              <th class="text-right">{{ t('equipment.fields.amount.label') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(equipment, index) in delivery.equipments"
              :key="`deliveryOrderEquipment-${index}`"
              :e2e-row="'deliveryOrderEquipment-' + index"
            >
              <td e2e-data="deliveryOrderEquipmentSupplierName">
                {{ equipment.supplier?.name ?? '' }}
              </td>
              <td e2e-data="deliveryOrderEquipmentBrand">{{ equipment.brand }}</td>
              <td e2e-data="deliveryOrderEquipmentModel">{{ equipment.model }}</td>
              <td e2e-data="deliveryOrderEquipmentSerialNumber">{{ equipment.serialNumber }}</td>
              <td class="text-right" e2e-data="deliveryOrderEquipmentUnitAmount">
                {{ n(Number(equipment.unitAmount), 'currency') }}
              </td>
              <td class="text-right" e2e-data="deliveryOrderEquipmentQuantity">{{ equipment.quantity }}</td>
              <td class="text-right" e2e-data="deliveryOrderEquipmentAmount">
                {{ n(Number(equipment.amount), 'currency') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="delivery.equipments.length != 0" class="row">
        <div class="col-md-4">
          <p v-if="delivery.deliveredAt">
            <b>{{ t('equipment.fields.deliveredAt.title') }}</b> :
            <span e2e-data="deliveryOrderDeliveredAt">{{ d(new Date(delivery.deliveredAt)) }}</span>
          </p>
        </div>
        <div class="col-md-4">
          <p v-if="delivery.deliverySignedAt" class="ml-3">
            <b>{{ t('equipment.fields.deliverySignedAt.label') }}</b> :
            <span e2e-data="deliveryOrderSignedAt">{{ d(new Date(delivery.deliverySignedAt)) }}</span>
          </p>
        </div>
      </div>

      <template v-if="fieldLoaded && formRef && delivery.equipments.length > 0">
        <div v-if="!delivery.deliveredAt" class="row">
          <div class="col-md-6">
            <FieldCreator
              :form-data="formRef"
              name="deliveredAt"
              :form-ref="'formdelivery-' + delivery.id"
              domain="equipment"
              label-left
            />
          </div>
        </div>
        <div v-if="formDef?.operationalAt" class="row">
          <div class="col-md-6">
            <FieldCreator
              :form-data="formRef"
              name="operationalAt"
              :form-ref="'formdelivery-' + delivery.id"
              domain="equipment"
              label-left
            />
          </div>
        </div>
        <div v-if="hasEditableField" class="row">
          <div class="col-md-6">
            <ButtonAll
              type="validation"
              icon="calendar"
              label="button.save"
              name="deliveryUpdate"
              :loading="sending"
              class="mt-2"
              @click="updateDeliveryOrder"
            />
          </div>
        </div>
      </template>

      <ContractDeliveryOrderSignature :delivery="delivery" :contract-number="contractNumber" :uuid="uuid" />
    </BlockContent>
  </div>
</template>

<style scoped></style>
